import React, { useState } from 'react';
import { Box, Card, CardContent, Typography, Chip, Button, Alert, Tabs, Tab, TextField, Select, MenuItem, FormControl, InputLabel, Stack, LinearProgress } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useUserMetadata } from '../context/UserMetadataContext';
import { PageHeader } from '../components/shared';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';

const EvidencePage = ({ section = 'compliance' }) => {
  const { userMetadata } = useUserMetadata();
  void userMetadata;
  const [activeTab, setActiveTab] = useState(section === 'compliance' ? 0 : section === 'timeline' ? 1 : 2);

  const frameworks = [
    { id: 'soc2', name: 'SOC 2 Type II', status: 'in_progress', controls: 87, passing: 72, lastAudit: '2025-10-15' },
    { id: 'iso27001', name: 'ISO 27001', status: 'compliant', controls: 114, passing: 108, lastAudit: '2025-09-20' },
    { id: 'nist', name: 'NIST CSF', status: 'in_progress', controls: 98, passing: 65, lastAudit: '2025-11-01' },
    { id: 'gdpr', name: 'GDPR', status: 'needs_review', controls: 42, passing: 35, lastAudit: '2025-08-10' },
  ];

  const getStatusChip = (status) => {
    switch (status) {
      case 'compliant':
        return <Chip label="Compliant" color="success" size="small" />;
      case 'in_progress':
        return <Chip label="In Progress" color="warning" size="small" />;
      case 'needs_review':
        return <Chip label="Needs Review" color="error" size="small" />;
      default:
        return <Chip label="Unknown" size="small" />;
    }
  };

  const getProgressColor = (status) => {
    switch (status) {
      case 'compliant': return 'success';
      case 'needs_review': return 'error';
      default: return 'warning';
    }
  };

  return (
    <Box>
      <PageHeader
        title="Evidence & Compliance"
        description="Audit-Ready Evidence Fabric — Time-bound access snapshots, identity timelines, and exportable evidence packages"
        pillarId="evidence"
      />

      {/* Mission Statement */}
      <Card sx={{ mb: 3, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(93, 168, 122, 0.08)' : 'rgba(93, 168, 122, 0.05)' }}>
        <CardContent sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <AssignmentIcon sx={{ color: 'success.main' }} />
          <Typography variant="body2">
            <strong>Evidence Fabric Mission:</strong> Convert trust intelligence into framework-agnostic evidence by design.
            Answer "who had access to what, when, and was it appropriate?"
          </Typography>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<AssignmentIcon />} iconPosition="start" label="Compliance Dashboard" />
        <Tab icon={<AccessTimeIcon />} iconPosition="start" label="Identity Timeline" />
        <Tab icon={<FileDownloadIcon />} iconPosition="start" label="Export Evidence" />
      </Tabs>

      {/* Compliance Dashboard Tab */}
      {activeTab === 0 && (
        <Box>
          <Grid2 container spacing={2} sx={{ mb: 3 }}>
            {frameworks.map((framework) => (
              <Grid2 xs={12} sm={6} lg={3} key={framework.id}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2">{framework.name}</Typography>
                      {getStatusChip(framework.status)}
                    </Stack>
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Controls Passing</Typography>
                        <Typography variant="caption" color="text.secondary">{framework.passing}/{framework.controls}</Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={(framework.passing / framework.controls) * 100}
                        color={getProgressColor(framework.status)}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">Last Audit: {framework.lastAudit}</Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid2>
            ))}
          </Grid2>

          <Alert severity="info" icon={<InfoIcon />}>
            <strong>Coming Soon:</strong> Detailed control mapping, evidence collection status, and automated compliance reports.
            Framework data is currently illustrative.
          </Alert>
        </Box>
      )}

      {/* Identity Timeline Tab */}
      {activeTab === 1 && (
        <Card>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">Identity Timeline</Typography>
          </Box>
          <CardContent>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <strong>Coming Soon:</strong> Identity timeline view showing all access changes, role modifications,
              and trust score changes over time for any user.
            </Alert>

            <Box sx={{ textAlign: 'center', py: 5 }}>
              <AccessTimeIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Identity Timeline</Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Search for a user to view their complete access history and trust score timeline.
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 2 }}>
                <TextField placeholder="Enter user email..." disabled sx={{ maxWidth: 400 }} size="small" />
                <Button variant="contained" disabled>Search</Button>
              </Stack>
              <Typography variant="caption" color="text.disabled">Feature in development</Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Export Evidence Tab */}
      {activeTab === 2 && (
        <Grid2 container spacing={2}>
          <Grid2 xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6">Export Evidence Package</Typography>
              </Box>
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Generate audit-ready evidence packages for compliance frameworks.
                </Typography>

                <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled>
                  <InputLabel>Select Framework</InputLabel>
                  <Select label="Select Framework" defaultValue="soc2">
                    <MenuItem value="soc2">SOC 2 Type II</MenuItem>
                    <MenuItem value="iso27001">ISO 27001</MenuItem>
                    <MenuItem value="nist">NIST CSF</MenuItem>
                    <MenuItem value="gdpr">GDPR</MenuItem>
                  </Select>
                </FormControl>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Date Range</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <TextField type="date" size="small" disabled fullWidth />
                  <Typography variant="body2" color="text.secondary">to</Typography>
                  <TextField type="date" size="small" disabled fullWidth />
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Export Format</Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Button variant="outlined" disabled startIcon={<FileDownloadIcon />}>PDF</Button>
                  <Button variant="outlined" disabled startIcon={<FileDownloadIcon />}>CSV</Button>
                  <Button variant="outlined" disabled startIcon={<FileDownloadIcon />}>JSON</Button>
                </Stack>

                <Alert severity="info">Export functionality coming soon</Alert>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6">Point-in-Time Query</Typography>
              </Box>
              <CardContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Answer "Who had access to X on date Y?" — essential for audit responses.
                </Typography>

                <TextField label="Resource/Application" placeholder="e.g., Production Database" size="small" fullWidth disabled sx={{ mb: 2 }} />
                <TextField label="Point in Time" type="datetime-local" size="small" fullWidth disabled sx={{ mb: 2 }} InputLabelProps={{ shrink: true }} />

                <Button variant="contained" disabled fullWidth startIcon={<SearchIcon />}>Query Access</Button>

                <Alert severity="info" sx={{ mt: 2 }}>Historical queries require data snapshots (coming soon)</Alert>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>
      )}
    </Box>
  );
};

export default EvidencePage;
